import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AccountsService } from 'app/modules/accounts/accounts.service';
import { getSubdomain } from 'app/shared/Utils/common.utils';
import { UserState } from 'app/state/user/state';
import { TsSnackbarService } from 'tsui';
import { environment } from '@env/environment';
import { catchError, map, of, take } from 'rxjs';

const FORBIDDEN_ACCESS_MESSAGE = 'Access is forbidden.';

@Injectable({
  providedIn: 'root',
})
export class ForbiddenAccessHandler {
  private isHandlingForbiddenRedirect = false;

  private readonly snackbarService = inject(TsSnackbarService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly accountsService = inject(AccountsService);

  handle(): void {
    if (this.isHandlingForbiddenRedirect) {
      return;
    }

    this.isHandlingForbiddenRedirect = true;
    this.snackbarService.open(FORBIDDEN_ACCESS_MESSAGE, 'error');

    const cachedWorkspaceCount = this.getCachedWorkspaceCount();

    if (cachedWorkspaceCount > 1) {
      this.redirectToWorkspaceSelection();
      return;
    }

    if (cachedWorkspaceCount === 1) {
      this.signOut();
      return;
    }

    this.resolveWorkspaceCountAndRedirect();
  }

  resetHandlingState(): void {
    this.isHandlingForbiddenRedirect = false;
  }

  private resolveWorkspaceCountAndRedirect(): void {
    this.accountsService
      .getAccounts('name', 'asc', { skipForbiddenRedirect: true })
      .pipe(
        take(1),
        map((accounts) => accounts.length),
        catchError(() => of(0)),
      )
      .subscribe((workspaceCount) => {
        if (workspaceCount === 1) {
          this.signOut();
          return;
        }

        this.redirectToWorkspaceSelection();
      });
  }

  private getCachedWorkspaceCount(): number {
    const userAccounts = this.store.selectSnapshot(UserState.getUser)?.accounts ?? [];
    const cachedAccounts = this.accountsService.accountsValue ?? [];
    const accounts = userAccounts.length > 0 ? userAccounts : cachedAccounts;

    return accounts.length;
  }

  private redirectToWorkspaceSelection(): void {
    if (getSubdomain()) {
      window.location.href = `https://dashboard.${environment.appDomain}/accounts`;
      return;
    }

    this.router.navigate(['/accounts']);
  }

  private signOut(): void {
    if (getSubdomain()) {
      window.location.href = `https://dashboard.${environment.appDomain}/sign-out`;
      return;
    }

    window.location.assign('/sign-out');
  }
}
